<template>
  <div v-if="blog" class="space-y-4">

    <div class="flex flex-wrap gap-x-2">
      <label class="w-full font-semibold text-sm text-slate-600" v-text="'Published at'" />
      <input
        v-model="blog.published_at"
        type="datetime-local"
        class="!w-min flex-grow"
        >
      <ui-button class="dark flex-grow md:max-w-24" @click="blog.published_at = moment().utc().format('YYYY-MM-DDTHH:mm:ss')">
        Now
      </ui-button>
    </div>

    <div>
      <ui-language-switcher-input
        v-model="blog.editing_language"
        />
    </div>

    <div>
      <label class="font-semibold text-sm text-slate-600">Title (SEO & browser tab)</label>

      <div class="flex gap-2 items-center w-full">
        <div class="flex-grow">
          <ui-text v-model="computedTitle"/>
        </div>
        <ui-switch v-if="blog.meta" v-model="blog.meta.show_title_as_h1">
          Show as H1
        </ui-switch>
      </div>
    </div>

    <div>
      <ui-text
        v-model="computedDescription"
        label="Description (SEO & excerpt)"
        rows="5"
        />
    </div>

    <div>
      <ui-text
        v-model="computedURL"
        label="URL (Instead of 404)"
        />
    </div>

    <div>
      <ui-text
        v-model="computedAnchor"
        label="Anchor (SEO summary)"
        />
    </div>

    <div>
      <ui-select
        v-model="blog.layout"
        label="Layout (Page layout)"
        >
        <ui-select-option
          v-for="(layout,l) in availableLayouts"
          :key="l"
          :default="layout.default"
          :text="layout.name"
          :value="layout.value"
          />
      </ui-select>
    </div>

    <div v-if="blog.meta && blog.meta.languages">
      <ui-select
        v-model="blog.meta.languages"
        label="Limit languages"
        multiple
        placeholder="shown in all languages if empty"
        >
        <ui-select-option
          key="en"
          value="en"
          text="en"
          />
        <ui-select-option
          v-for="(language,ll) in availableLocales"
          :key="ll"
          :value="language.code"
          :text="language.code"
          />
      </ui-select>
    </div>

    <blog-settings-tags v-model="blog"/>
  </div>
</template>

<script setup lang="ts">
import moment from 'moment'

const { locale, locales } = useI18n()
const blog = defineModel<Blog>()

onMounted(() => {
  if (blog.value?.meta && !blog.value.meta?.languages) {
    blog.value.meta.languages = []
  }
})

const computedTitle = computed({
  get: () => {
    if(!blog.value) return ''

    return ((blog.value.editing_language ?? locale.value) != 'en')
      ? (blog.value.meta.i18n[blog.value.editing_language ?? locale.value]?.title
        ? blog.value.meta.i18n[blog.value.editing_language ?? locale.value]?.title
        : blog.value.title
      )
      : blog.value.title
  },
  set: (value) => {
    if(!blog.value) return

    if ((blog.value.editing_language ?? locale.value) != 'en') {
      blog.value.meta.i18n[blog.value.editing_language ?? locale.value].title = value
    } else {
      blog.value.title = value
    }
  },
})

const computedDescription = computed({
  get: () => {
    if(!blog.value) return ''

    return ((blog.value.editing_language ?? locale.value) != 'en')
      ? (blog.value.meta.i18n[blog.value.editing_language ?? locale.value]?.description
        ? blog.value.meta.i18n[blog.value.editing_language ?? locale.value]?.description
        : blog.value.description
      )
      : blog.value.description
  },
  set: (value) => {
    if(!blog.value) return

    if ((blog.value.editing_language ?? locale.value) != 'en') {
      blog.value.meta.i18n[blog.value.editing_language ?? locale.value].description = value
    } else {
      blog.value.description = value
    }
  },
})

const computedURL = computed({
  get: () => {
    if(!blog.value) return ''

    return ((blog.value.editing_language ?? locale.value) != 'en')
      ? (blog.value.meta.i18n[blog.value.editing_language ?? locale.value]?.url
        ? blog.value.meta.i18n[blog.value.editing_language ?? locale.value]?.url
        : blog.value.url
      )
      : blog.value.url
  },
  set: (value) => {
    if(!blog.value) return

    if ((blog.value.editing_language ?? locale.value) != 'en') {
      blog.value.meta.i18n[blog.value.editing_language ?? locale.value].url = value
    } else {
      blog.value.url = value
    }
  },
})

const computedAnchor = computed({
  get: () => {
    if(!blog.value) return ''

    return ((blog.value.editing_language ?? locale.value) != 'en')
      ? (blog.value.meta.i18n[blog.value.editing_language ?? locale.value]?.anchor
        ? blog.value.meta.i18n[blog.value.editing_language ?? locale.value]?.anchor
        : blog.value.anchor
      )
      : blog.value.anchor
  },
  set: (value) => {
    if(!blog.value) return

    if ((blog.value.editing_language ?? locale.value) != 'en') {
      blog.value.meta.i18n[blog.value.editing_language ?? locale.value].anchor = value
    } else {
      blog.value.anchor = value
    }
  },
})

const availableLocales = computed(() => {
  return locales.value.filter(i => i.code !== locale.value && i.code !== 'ru')
})
</script>
